// Fills the `{{token}}` slots of a user-prompt template (`ClinicalReportPromptVersion.userPromptFree`
// / `userPromptTemplate`, or the shipped `CLINICAL_*_USER_PROMPT` fallbacks). Pure, no Angular --
// consumed by `ClinicalReportRepositoryImpl` to compose the prompt a generation sends, and by the
// AI report modal to interpolate a template a developer edited before running it.

import { htmlToPlainText } from './clinical-html.util';
import { describePlaceholders, hasPlaceholders } from './template-placeholder.util';

export type PromptTemplateValues = Readonly<Record<string, string | null | undefined>>;

export interface PromptTemplateContext {
  readonly freeText: string;
  readonly interfaceLanguage: string;
  readonly consultation: Record<string, unknown>;
  readonly templateText: string | null;
}

// The single definition of what each token stands for: the repository and the modal must fill an
// edited prompt exactly the same way, or the preview would stop matching what gets sent.
//
// `notas_seleccionadas` is flattened to plain text because ProVet stores notes as rich text, and a
// wall of `<p>`/`<td>` markup buys the model nothing while costing tokens the answer needs.
// `plantilla`, in contrast, stays as stored: the answer must come back as the same HTML, tables
// included, since `uv-rich-text` renders it and the vet pastes it back into ProVet's editor.
export function buildPromptTemplateValues(context: PromptTemplateContext): PromptTemplateValues {
  const { templateText } = context;

  return {
    notas_seleccionadas: htmlToPlainText(context.freeText),
    idioma_interfaz: context.interfaceLanguage,
    contexto_consulta: JSON.stringify(context.consultation),
    plantilla: templateText ?? '',
    huecos: templateText === null ? '' : describePlaceholders(templateText),
    modo_plantilla: templateText === null ? '' : describeTemplateMode(templateText),
  };
}

// Which of the two template shapes this generation is dealing with, as a phrase the prompt can name
// directly. Exposed as a token rather than as a second system instruction because
// `ClinicalReportPromptVersion` stores exactly two prompt pairs (free and template), and a saved
// revision must keep working without a schema change.
function describeTemplateMode(templateText: string): string {
  return hasPlaceholders(templateText)
    ? 'plantilla con huecos'
    : 'plantilla esqueleto de secciones';
}

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

// Deliberately tolerant in both directions, because the templates are edited by hand in a
// developer tool and a typo must never leave a generation without a prompt: an unknown token is
// left verbatim (so the mistake is visible in the preview instead of silently vanishing), while a
// known token whose value is absent collapses to an empty string.
export function interpolatePromptTemplate(template: string, values: PromptTemplateValues): string {
  return template.replace(TOKEN_PATTERN, (raw, token: string) =>
    Object.prototype.hasOwnProperty.call(values, token) ? (values[token] ?? '') : raw,
  );
}
